import { AxiosResponse } from 'axios';
import { PAGE_SIZE } from '~/constants';
import {
  Company,
  CompanyForm,
  CompanyView,
  Pagination,
  SelectItem,
  User,
} from '~/types';
import api from '~/utils/api';
import { isActive } from '~/utils/isActive';
import { CompanySelectItem } from './types';
import { SalesSegment } from '~/types/SalesSegment';
import apiAssist from '~/utils/apiAssist';

export function addFormattedPropertiesToCompany(company: Company): CompanyView {
  return {
    ...company,
    isHeadOffice: company.nu_cnpjordemraiz === '0001',
    status: Boolean(Number(company.tp_situacao)),
    isActive: isActive(Boolean(Number(company.tp_situacao))),
    isInvoicePayer: Boolean(Number(company.tp_pagadorfatura)),
  };
}

export async function getCompanies(
  page: number,
  search: string,
  orderby: string,
  order: string,
  pageSize: number = PAGE_SIZE,
): Promise<Pagination<CompanyView>> {
  const { data } = await api.get<Pagination<Company>>('/empresas', {
    params: {
      page,
      size: pageSize,
      orderby,
      order,
      ...(search ? { search } : {}),
    },
  });

  const companies = data.results.map(company =>
    addFormattedPropertiesToCompany(company),
  );

  return {
    ...data,
    results: companies,
  };
}

export async function getCompanyById(id: number): Promise<CompanyView> {
  const { data } = await api.get<Company>(`/empresas/${id}`);

  const company = addFormattedPropertiesToCompany(data);

  return company;
}

export async function getCompanyAddressById(id: string): Promise<Company> {
  const { data } = await apiAssist.get<Company>(`/empresa/${id}`);
  return data;
}

export async function getCompaniesAsSelect(
  groupId: string | null,
): Promise<CompanySelectItem[]> {
  const { data } = await api.get(`/empresa_sem_grupo`, {
    params: {
      ...(groupId && { groupId }),
    },
  });
  return data;
}

export async function getCompaniesByGroupAsSelect(
  groupId: number,
  pageSize: number = PAGE_SIZE,
): Promise<CompanySelectItem[]> {
  const { data } = await api.get<Company[]>(
    `/grupoeconomico/${groupId}/empresas`,
    {
      params: {
        size: pageSize,
      },
    },
  );

  const companies = data
    .filter(item => Boolean(Number(item.tp_situacao)) === true)
    .map(item => ({
      value: String(item.id_empresa),
      label: `${item.cnpjFormatado} | ${item.nm_fantasia || '-'}`,
      isHeadOffice: item.nu_cnpjordemraiz === '0001',
      hasGroup: Boolean(item.id_grupoeconomico),
      id_grupoeconomico: item.id_grupoeconomico,
      codigo: item.cd_usuariosap,
      cnpj: item.cnpjFormatado,
      name: `${item.nm_fantasia || '-'}`,
    }));

  return companies;
}

export async function updateCompanyById(
  id: number,
  data: CompanyForm,
): Promise<AxiosResponse<any>> {
  return api.put(`/empresas/${id}`, data);
}

export async function getSalesSegmentAsSelect(): Promise<SelectItem[]> {
  const { data } = await api.get(`/segmento_venda`);

  const list = data.map((item: SalesSegment) => ({
    label: item.nome,
    value: item.id,
  }));

  return list;
}

export async function getCustomerSuccessAsSelect(): Promise<SelectItem[]> {
  const { data } = await api.get(`/user_corporate/customer_success`);

  const list = data.map((item: User) => ({
    label: item.name,
    value: item.id,
  }));

  return list;
}

export async function getAccountManagerAsSelect(
  id: number,
): Promise<SelectItem[]> {
  const { data } = await api.get(
    `/user_corporate/gerente_segmento_venda?id_segmento_venda=${id}`,
  );

  const list = data.map((item: User) => ({
    label: item.name,
    value: item.id,
  }));

  return list;
}

export async function getCompaniesXlsx(
  search: string,
  orderby = 'nm_grupoeconomico',
  order: string,
): Promise<AxiosResponse<any>> {
  const url = '/empresas/xlsx';
  return api.get(url, {
    params: {
      orderby,
      order,
      ...(search ? { search } : {}),
    },
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}
